import { formatPrimitive, humanizeKey, isPlainPrimitive, isProse, itemHeading, joinPrimitives } from "./humanizeData";

interface HumanizedDataProps {
  /** Arbitrary JSON-ish value from message metadata (tool input/output, event metadata). */
  data: unknown;
}

/**
 * Lossless human-readable rendering of agent-visible metadata — the
 * replacement for raw JSON dumps in the timeline's internal rows. Every key,
 * every array item and every character renders; only the format changes
 * (labelled rows instead of braces). A tall payload scrolls, never truncates.
 *
 * Timeline-only (rendered under {@link InvisibleRow}), so `body-*` classes are
 * safe here — the widget never mounts this component.
 */
export function HumanizedData({ data }: HumanizedDataProps): React.ReactElement {
  return (
    <div className="body-sm max-h-96 overflow-y-auto text-left text-grey-600">
      <DataNode value={data} />
    </div>
  );
}

/**
 * Whether a value fits on the same line as its key. Prose strings, objects and
 * arrays-of-objects get an indented block of their own instead.
 */
function isInline(value: unknown): boolean {
  if (isPlainPrimitive(value)) return !(typeof value === "string" && isProse(value));
  if (Array.isArray(value)) return value.length === 0 || value.every(isPlainPrimitive);
  return Object.keys(value as object).length === 0;
}

function DataNode({ value }: { value: unknown }): React.ReactElement {
  if (isPlainPrimitive(value)) {
    if (typeof value === "string" && isProse(value)) {
      return <p className="wrap-break-word whitespace-pre-wrap">{value}</p>;
    }
    return <span>{formatPrimitive(value)}</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span>—</span>;
    if (value.every(isPlainPrimitive)) return <span>{joinPrimitives(value)}</span>;
    return (
      <div className="flex flex-col gap-1.5">
        {value.map((item, index) => (
          <div key={index}>
            {isPlainPrimitive(item) ? (
              <DataNode value={item} />
            ) : (
              <>
                <p className="body-sm-alt text-grey-700">{itemHeading(item as Record<string, unknown>, index)}</p>
                <DataNode value={item} />
              </>
            )}
          </div>
        ))}
      </div>
    );
  }

  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return <span>—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {entries.map(([key, entryValue]) => (
        <div key={key}>
          <span className="body-mono text-grey-500">{humanizeKey(key)}: </span>
          {isInline(entryValue) ? (
            <DataNode value={entryValue} />
          ) : (
            <div className="mt-0.5 border-l border-grey-200 pl-3">
              <DataNode value={entryValue} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
